import * as _ from 'lodash-es';
import { DateTime } from 'luxon';

/**
 * Compare two objects and return the differences between them.
 * For example, if obj1 = { a: 1, b: 2 } and obj2 = { a: 1, b: 3 },
 * the result will be { b: 3 }.
 *
 * @param obj1 The first object to compare
 * @param obj2 The second object to compare
 * @returns The differences between the two objects
 */
export function diff<T extends Record<string, unknown>>(
  obj1: T,
  obj2: T,
): Record<string, unknown> {
  const changes: Record<string, unknown> = {};

  const keys = _.union(Object.keys(obj1), Object.keys(obj2)); // Get union of both object keys

  keys.forEach((key) => {
    const value1 = obj1[key];
    const value2 = obj2[key];

    // Specific check for dates
    if (_.isDate(value1) && _.isDate(value2)) {
      if (value1.getTime() !== value2.getTime()) {
        changes[key] = value2;
      }
    }
    // Specific check for DateTime objects
    else if (value1 instanceof DateTime && value2 instanceof DateTime) {
      if (!value1.equals(value2)) {
        changes[key] = value2;
      }
    }
    // Specific check for arrays
    else if (_.isArray(value1) && _.isArray(value2)) {
      if (value1.length !== value2.length || !_.isEqual(value1, value2)) {
        changes[key] = value2;
      }
    }
    // Check for objects (excluding arrays and null values)
    else if (
      _.isObject(value1) &&
      !_.isArray(value1) &&
      !_.isDate(value1) &&
      _.isObject(value2) &&
      !_.isArray(value2) &&
      !_.isDate(value2)
    ) {
      const nestedChanges = diff(
        value1 as Record<string, unknown>,
        value2 as Record<string, unknown>,
      );
      if (!_.isEmpty(nestedChanges)) {
        changes[key] = nestedChanges;
      }
    }
    // Fallback for other types and direct comparison
    else if (!_.isEqual(value1, value2)) {
      changes[key] = value2;
    }
  });

  return changes;
}
